"use client";

import { ApiClient } from "@/lib/api-client";
import { storage } from "@/lib/storage-manager";
import { Business, User } from "@/types";
import { useRouter } from "next/navigation";
import { createContext, useContext, useEffect, useState } from "react";

// Define your API response types
interface LoginResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: User;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  businesses: Business[] | null;
  currentBusiness: Business | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  refreshAccessToken: () => Promise<string | null>;
  setUser: (user: User | null) => void;
  logout: () => void;
  register: (userData: User | FormData) => Promise<void>;
  fetchUserBusinesses: () => Promise<void>;
  setBusinesses: (business: Business[]) => void;
  setCurrentBusiness: (business?: Business | null) => void;
  setError: (error: string | null) => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);

  const [businesses, setBusinesses] = useState<Business[] | null>(null);
  const [currentBusiness, setCurrentBusinessState] = useState<Business | null>(
    null,
  );
  // Wrapper to match AuthState signature
  const setCurrentBusiness = (business?: Business | null) => {
    setCurrentBusinessState(business ?? null);
    storage.setCurrentBusiness(business ?? null);
  };
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  // check for existing token on mount
  useEffect(() => {
    const storedUser = storage.getUser();
    const storedAccessToken = storage.getAccessToken();
    const storedRefreshToken = storage.getRefreshToken();

    if (storedUser && storedAccessToken) {
      setUser(storedUser);
      setAccessToken(storedAccessToken);
      setRefreshToken(storedRefreshToken);

      // Sync cookie from localStorage to ensure proxy/middleware can access it
      // Set cookie with 7 days expiry for persistent login
      const maxAge = 7 * 24 * 60 * 60; // 7 days in seconds
      document.cookie = `token=${storedAccessToken}; path=/; max-age=${maxAge}; SameSite=Lax`;
    }

    const storedBusinesses = storage.getBusinesses();
    const storedCurrentBusiness = storage.getCurrentBusiness();

    if (storedBusinesses !== null) {
      setBusinesses(storedBusinesses);
      setCurrentBusiness(storedCurrentBusiness);

      // Sync currentBusinessId cookie as well
      if (storedCurrentBusiness?.id) {
        const maxAge = 7 * 24 * 60 * 60; // 7 days in seconds
        document.cookie = `currentBusinessId=${storedCurrentBusiness.id}; path=/; max-age=${maxAge}; SameSite=Lax`;
      }
    }

    setIsLoading(false);
  }, []);

  // Fetch user businesses
  const fetchUserBusinesses = async () => {
    const token = storage.getAccessToken();
    const userID = storage.getUser()?.id;
    if (!token || !userID) return;

    setIsLoading(true);
    try {
      const data = await ApiClient.get<Business[]>(
        "businesses/user/" + userID,
        token,
      );
      setBusinesses(data || []);
      // storage.setBusinesses(data || []);

      // Set first business as default if available and not already set
      // if (data && data.length > 0) {
      //   setCurrentBusinessState(data[0]);
      //   storage.setCurrentBusiness(data[0]);
      // }
    } catch (error) {
      console.error("Error fetching businesses:", error);
      setError("Failed to fetch businesses.");
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (
    email: string,
    password: string,
    remember: boolean = false,
  ): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await ApiClient.post<LoginResponse>("auth/login", {
        identifier: email,
        password,
      });

      // Save to storage using storage manager
      storage.setAuthTokens(data.access_token, data.refresh_token, remember);
      storage.setUser(data.user, remember);

      // Set cookies for middleware access (7 days for persistent login)
      // Only set persistent cookie if remember is true, otherwise let it be session cookie
      const maxAge = remember ? 7 * 24 * 60 * 60 : undefined; // 7 days in seconds if remember
      const cookieOptions = maxAge
        ? `max-age=${maxAge}; SameSite=Lax`
        : `SameSite=Lax`; // Session cookie if not remembered
        
      document.cookie = `token=${data.access_token}; path=/; ${cookieOptions}`;

      // Store tokens and user data
      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);
      setUser(data.user);

      // Handle redirect
      const params = new URLSearchParams(window.location.search);
      const returnUrl = params.get("returnUrl");
      if (returnUrl) {
        router.push(returnUrl);
      } else {
        router.push("/businesses");
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Login error:", error);
      setIsLoading(false);
      setError("Invalid credentials. Please try again.");
    }
  };

  const register = async (userData: User | FormData) => {
    setIsLoading(true);
    try {
      let data: LoginResponse;

      if (userData instanceof FormData) {
        // Handle FormData with image upload
        data = await ApiClient.postFormData<LoginResponse>(
          "auth/register",
          userData,
          undefined,
          { includeBusinessId: false },
        );
      } else {
        // Handle regular JSON data
        data = await ApiClient.post<LoginResponse>("auth/register", userData);
      }

      storage.setAuthTokens(data.access_token, data.refresh_token);
      storage.setUser(data.user);

      // Set cookies for middleware access (7 days for persistent login)
      const maxAge = 7 * 24 * 60 * 60; // 7 days in seconds
      document.cookie = `token=${data.access_token}; path=/; max-age=${maxAge}; SameSite=Lax`;

      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);
      setUser(data.user);
    } catch (error) {
      console.error("Registration error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // Clear state
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    setBusinesses(null);
    setCurrentBusiness(null);

    // Clear storage using storage manager
    storage.clearUserData();

    // Clear cookies
    document.cookie = "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    document.cookie =
      "currentBusinessId=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    // Redirect to login
    router.push("/login");
  };

  const refreshAccessToken = async (): Promise<string | null> => {
    if (!refreshToken) return null;

    try {
      const data = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}auth/refresh-token`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: refreshToken }),
        },
      ).then((res) => {
        if (!res.ok) throw new Error("Token refresh failed");
        return res.json();
      });

      if (!data) return null;

      // Update storage
      storage.setAuthTokens(data.access_token, data.refresh_token);
      storage.setUser(data.user);

      // Update state
      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);
      setUser(data.user);

      // Set cookies for middleware access (7 days for persistent login)
      const maxAge = 7 * 24 * 60 * 60; // 7 days in seconds
      document.cookie = `token=${data.access_token}; path=/; max-age=${maxAge}; SameSite=Lax`;

      return data.access_token;
    } catch (error) {
      console.error("refresh error:", error);
      logout();
      return null;
    }
  };

  // set up the refresh token callback when the auth context initializes
  useEffect(() => {
    // Set the refresh token callback in the ApiClient
    ApiClient.setRefreshTokenCallback(refreshAccessToken);
  }, [refreshToken]); // Re-run if refresh token changes

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        refreshToken,
        setBusinesses,
        setUser,
        businesses,
        currentBusiness,
        isAuthenticated: !!user,
        isLoading,
        error,
        login,
        refreshAccessToken,
        register,
        logout,
        fetchUserBusinesses,
        setCurrentBusiness,
        setError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
